import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Seo from "@/components/Seo";
import InlineQuizCard from "@/components/InlineQuizCard";
import { api } from "@/lib/api";
import { rememberArticle, trackEvent } from "@/lib/attribution";

function renderBody(body) {
  const parts = body.split(/\n\n+/).filter((p) => p.trim());
  // Insert the inline CTA roughly halfway through any article with 3+ blocks.
  // Mid is computed in characters (not block count) so the CTA falls after
  // ~50% of read content, which is the highest-converting spot.
  let mid = -1;
  if (parts.length >= 3) {
    const total = parts.reduce((sum, p) => sum + p.length, 0);
    let acc = 0;
    for (let i = 0; i < parts.length; i += 1) {
      acc += parts[i].length;
      if (acc >= total / 2) { mid = i; break; }
    }
  }
  const out = [];
  parts.forEach((block, idx) => {
    if (block.startsWith("## ")) {
      out.push(
        <h2 key={`h-${idx}`} className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] mt-10 mb-3">
          {block.replace(/^##\s*/, "")}
        </h2>
      );
    } else {
      out.push(
        <p key={`p-${idx}`} className="text-[#1A2E24] text-lg leading-relaxed mb-5 whitespace-pre-line">
          {block}
        </p>
      );
    }
    if (idx === mid) {
      out.push(<InlineQuizCard key="inline-cta" />);
    }
  });
  return out;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/articles/public/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true));
    rememberArticle(slug);
    trackEvent("article_view", { article_slug: slug, page: `/articles/${slug}` });
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Seo title="Article not found" path={`/articles/${slug}`} noindex />
        <PublicNav />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display text-3xl text-[#1A2E24] mb-4">Article not found</h1>
          <Link to="/articles" className="text-[#2C4C3B] font-medium underline">Back to all articles</Link>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <PublicNav />
        <div className="max-w-3xl mx-auto px-6 py-24">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title={article.title}
        description={article.hook || article.excerpt || `Read "${article.title}" on CareHomeConnect — honest, easy-to-read senior care guidance for families.`}
        path={`/articles/${article.slug}`}
        type="article"
        image={article.cover_image}
        keywords={article.category ? `${article.category}, senior care, assisted living, memory care, family caregiver` : null}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.hook || article.excerpt || article.title,
          "image": article.cover_image ? [article.cover_image] : undefined,
          "datePublished": article.created_at || undefined,
          "dateModified": article.updated_at || article.created_at || undefined,
          "author": {
            "@type": "Organization",
            "name": "CareHomeConnect"
          },
          "publisher": {
            "@type": "Organization",
            "name": "CareHomeConnect",
            "logo": {
              "@type": "ImageObject",
              "url": "https://carehomeconnect.org/og-image.jpg"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://carehomeconnect.org/articles/${article.slug}`
          },
          "articleSection": article.category || undefined
        }}
      />
      <PublicNav />

      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-10 md:pt-16">
        <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2C4C3B] mb-6">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>
        <div className="text-xs font-semibold text-[#9CAF88] uppercase tracking-wider mb-4">{article.category}</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A2E24] tracking-tight leading-[1.1]">
          {article.title}
        </h1>
        <p className="mt-5 text-xl text-[#5C6B62] leading-relaxed italic">{article.hook}</p>
        {article.cover_image && (
          <img
            src={article.cover_image}
            alt={article.title}
            onError={(e) => {
              const fb = "https://images.unsplash.com/photo-1758691031958-29541f3207a5?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";
              if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
            }}
            className="w-full aspect-[16/9] object-cover rounded-3xl mt-10 shadow-[0_12px_40px_rgba(44,76,59,0.1)]"
          />
        )}

        <div className="mt-10" data-testid="article-body">
          {renderBody(article.body)}
        </div>

        {/* Soft CTA */}
        <div className="mt-16 mb-20 bg-white rounded-3xl p-8 md:p-12 border border-[#E2DFD8] text-center">
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24] max-w-lg mx-auto">
            Find the Best Care Option for Your Loved One
          </h3>
          <p className="mt-4 text-[#5C6B62] max-w-md mx-auto">
            Take our gentle 3-minute quiz and get a personalized recommendation — no pressure, no cost.
          </p>
          <Link to="/quiz" data-testid="article-cta-quiz">
            <Button className="mt-6 bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 px-8 font-medium">
              Start the quiz <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </article>

      <PublicFooter />
    </div>
  );
}
